/**
 * Sprint 20 B2+B4+B6: EU Localization Settings
 *
 * Language, timezone (Intl.supportedValuesOf), currency with locale-aware
 * formatting, date format, VAT configuration. Timezone-aware scheduling preview.
 * Real apiFetch. CSS vars only.
 */
import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "@/lib/api-client";
import { Globe, Save, Clock, CheckCircle2 } from "lucide-react";
import { useTranslation, type SupportedLanguage } from "@/i18n";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "fr", label: "Français" },
  { code: "ro", label: "Română" },
  { code: "es", label: "Español" },
  { code: "nl", label: "Nederlands" },
];

const CURRENCIES = [
  { code: "EUR", symbol: "€", label: "Euro", locale: "de-DE" },
  { code: "USD", symbol: "$", label: "US Dollar", locale: "en-US" },
  { code: "GBP", symbol: "£", label: "British Pound", locale: "en-GB" },
  { code: "RON", symbol: "lei", label: "Romanian Leu", locale: "ro-RO" },
  { code: "CHF", symbol: "CHF", label: "Swiss Franc", locale: "de-CH" },
];

const DATE_FORMATS = [
  { value: "DD/MM/YYYY", example: "26/03/2026" },
  { value: "MM/DD/YYYY", example: "03/26/2026" },
  { value: "YYYY-MM-DD", example: "2026-03-26" },
];

/** Memory #34: Use Intl.supportedValuesOf for timezone list — never hardcode */
const TIMEZONES: string[] = (() => {
  try {
    return Intl.supportedValuesOf("timeZone");
  } catch {
    return [
      "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Bucharest",
      "Europe/Madrid", "Europe/Rome", "Europe/Amsterdam", "Europe/Zurich",
      "America/New_York", "America/Chicago", "America/Los_Angeles",
      "Asia/Tokyo", "Australia/Sydney",
    ];
  }
})();

interface LocalizationSettings {
  language: string;
  timezone: string;
  currency: string;
  dateFormat: string;
  vatNumber: string;
  vatRate: string;
}

export default function LocalizationPage() {
  const { setLocale } = useTranslation();

  const [settings, setSettings] = useState<LocalizationSettings>({
    language: "en",
    timezone: "Europe/London",
    currency: "EUR",
    dateFormat: "DD/MM/YYYY",
    vatNumber: "",
    vatRate: "19",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tzSearch, setTzSearch] = useState("");

  const fetchSettings = useCallback(async () => {
    try {
      const res = await apiFetch<{ data: LocalizationSettings }>("/settings/localization");
      setSettings(res.data);
    } catch { /* use defaults */ }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    setSaved(false);
    try {
      await apiFetch("/settings/localization", {
        method: "PUT",
        body: JSON.stringify(settings),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* error */ }
    finally { setIsSaving(false); }
  };

  const update = <K extends keyof LocalizationSettings>(key: K, value: LocalizationSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const selectedCurrency = CURRENCIES.find((c) => c.code === settings.currency);
  const now = new Date();

  /** Locale-aware currency preview (S20 B4) */
  const formatCurrencyPreview = (amount: number): string => {
    if (!selectedCurrency) return `${amount}`;
    try {
      return new Intl.NumberFormat(selectedCurrency.locale, {
        style: "currency",
        currency: selectedCurrency.code,
      }).format(amount);
    } catch {
      return `${selectedCurrency.symbol}${amount.toFixed(2)}`;
    }
  };

  /** Filtered timezone list for search */
  const filteredTimezones = tzSearch
    ? TIMEZONES.filter((tz) => tz.toLowerCase().includes(tzSearch.toLowerCase()))
    : TIMEZONES;

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-2xl">
        {[1, 2, 3, 4].map((i) => <SkeletonCard key={i} className="h-20" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-[var(--color-primary)]" />
          <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Localization</h1>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSaving ? <Skeleton className="h-4 w-4 bg-white/50" /> : saved ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
          {isSaving ? <Skeleton className="h-4 w-10 bg-white/50" /> : saved ? "Saved" : "Save"}
        </button>
      </div>

      {/* Language */}
      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Language</h2>
        <select
          value={settings.language}
          onChange={(e) => {
            update("language", e.target.value);
            setLocale(e.target.value as SupportedLanguage);
          }}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          All platform UI and notifications will use this language
        </p>
      </div>

      {/* Timezone */}
      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)] flex items-center gap-2">
          <Clock className="h-4 w-4" /> Timezone
        </h2>
        <input
          type="text"
          value={tzSearch}
          onChange={(e) => setTzSearch(e.target.value)}
          placeholder="Search timezones..."
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
        <select
          value={settings.timezone}
          onChange={(e) => update("timezone", e.target.value)}
          size={6}
          className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {filteredTimezones.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
        </select>
        <div className="flex items-center justify-between text-xs text-[var(--color-muted-foreground)]">
          <span>Current time in {settings.timezone}:</span>
          <span className="font-mono font-medium text-[var(--color-foreground)]">
            {now.toLocaleTimeString("en-GB", { timeZone: settings.timezone, hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </span>
        </div>
        <p className="text-xs text-[var(--color-muted-foreground)]">
          All scheduled items (campaigns, automations, webinars) will use this timezone.
          "Schedule for 9am" = 9am in YOUR timezone.
        </p>
      </div>

      {/* Currency + Date Format */}
      <div className="grid grid-cols-2 gap-4">
        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-3">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Currency</h2>
          <select
            value={settings.currency}
            onChange={(e) => update("currency", e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.symbol} {c.label}</option>)}
          </select>
          <div className="text-xs text-[var(--color-muted-foreground)]">
            Preview: {formatCurrencyPreview(1234.56)}
          </div>
        </div>

        <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-3">
          <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Date Format</h2>
          <select
            value={settings.dateFormat}
            onChange={(e) => update("dateFormat", e.target.value)}
            className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          >
            {DATE_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.value} ({f.example})</option>)}
          </select>
        </div>
      </div>

      {/* Number formatting preview (S20 B4) */}
      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-3">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)]">Formatting Preview</h2>
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div className="stagger-item">
            <span className="text-xs text-[var(--color-muted-foreground)]">Currency</span>
            <p className="font-mono font-medium text-[var(--color-foreground)]">{formatCurrencyPreview(9999.99)}</p>
          </div>
          <div className="stagger-item">
            <span className="text-xs text-[var(--color-muted-foreground)]">Large number</span>
            <p className="font-mono font-medium text-[var(--color-foreground)]">
              {new Intl.NumberFormat(selectedCurrency?.locale ?? "en-US").format(1234567)}
            </p>
          </div>
          <div className="stagger-item">
            <span className="text-xs text-[var(--color-muted-foreground)]">Percentage</span>
            <p className="font-mono font-medium text-[var(--color-foreground)]">
              {new Intl.NumberFormat(selectedCurrency?.locale ?? "en-US", { style: "percent", minimumFractionDigits: 1 }).format(0.2345)}
            </p>
          </div>
        </div>
      </div>

      {/* VAT */}
      <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5 space-y-4">
        <h2 className="text-sm font-semibold text-[var(--color-foreground)]">VAT Configuration</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[var(--color-muted-foreground)]">VAT Number</label>
            <input
              type="text"
              value={settings.vatNumber}
              onChange={(e) => update("vatNumber", e.target.value)}
              placeholder="e.g., RO12345678"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted-foreground)]">VAT Rate (%)</label>
            <input
              type="number"
              value={settings.vatRate}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (!Number.isNaN(v)) update("vatRate", e.target.value);
              }}
              min="0"
              max="30"
              className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] mt-1"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
